import { ColDef, RowClickedEvent } from 'ag-grid-community';
import { AgGridReact } from 'ag-grid-react';
import { useCallback, useEffect, useMemo, useState } from 'react';

import { aggridHeaderClass } from 'src/components/common/common';
import { GridContextWrapper } from 'src/components/common/gridWrapper/GridContextWrapper';
import Grid_Touch from 'src/components/common/gridWrapper/Grid_Touch';
import { useSYS3050_R01Store } from 'src/containers/mes/SYS/SYS3050_R01/Store_SYS3050_R01';
import { useCommonCodeStore } from 'src/store/useCommonCodeStore';
import { GridCustomColumnDefs } from 'src/utils/grid/GridCustomColumnDefs';
import { buildDefaultColumnStateFromDefs } from 'src/utils/grid/columnPinUtils';
import { useGridSettingContent } from 'src/utils/grid/GridSettingContent';
import { defaultColDef, getDefaultRowIndexColumnConfig } from 'src/utils/grid/gridStyleUtils';

import { SYS3050_R01Dto } from './Dto_SYS3050_R01';
import { onlyTouchGridStyle } from 'src/utils/grid/gridFunctionUtil';

const COLUMN_STATE_KEY = 'SYS3050_R01-grid-column-state';

interface Props {
	gridRef: React.RefObject<AgGridReact<SYS3050_R01Dto> | null>;
	defaultSYS3050_R01Row: SYS3050_R01Dto;
}

export const SYS3050_R01Table = (props: Props) => {
	const { gridRef, defaultSYS3050_R01Row } = props;

	const {
		SYS3050_R01List,
		selectedSYS3050_R01,
		setSelectedSYS3050_R01,
		initSelectedSYS3050_R01,
		columnState,
		setColumnState,
		scrollTop, setScrollTop,
	} = useSYS3050_R01Store();

	const { jwCdList, jcCdList, empstsCdList } = useCommonCodeStore();

	const [hoveredRowIndex, setHoveredRowIndex] = useState<number | null>(null);

	const {
		getRowId,
		settingRowIndex,
		createRowClassRules,
		paddedGridData,
		ensureSelectedRow,
	} = useGridSettingContent({
		gridRef,
		key: 'empNo',
		gridData: SYS3050_R01List,
		defaultGridData: defaultSYS3050_R01Row,
		selectedRow: selectedSYS3050_R01,
	});

	const headerNames: Record<string, string> = {
		rowIndex: '',
		empNo: '사원번호',
		jobPosition: '직책',
		empNm: '사원명',
		positionCd: '직위',
		empSts: '재직',
	};

	const getCellStyle = useCallback((params: unknown, key: string) => onlyTouchGridStyle(key, 'empNo')(params), []);

	const baseColumnDefs: ColDef<SYS3050_R01Dto>[] = useMemo(() => [
		...getDefaultRowIndexColumnConfig(),
		{
			headerName: '사원번호', colId: 'empNo', field: 'empNo', width: 95,
			headerClass: aggridHeaderClass,
			cellStyle: (p) => getCellStyle(p, 'empNo'),
		},
		{
			headerName: '사원명', colId: 'empNm', field: 'empNm', width: 88,
			headerClass: aggridHeaderClass,
			cellStyle: (p) => getCellStyle(p, 'empNm'),
		},
		{
			headerName: '직책', colId: 'jobPosition', field: 'jobPosition', width: 110,
			headerClass: aggridHeaderClass,
			valueGetter: (params) => {
				const jobPosition = params.data?.jobPosition;
				const foundData = jcCdList.find((value) => value.minorCd === jobPosition);
				return foundData?.codeNm;
			},
			cellStyle: (p) => getCellStyle(p, 'jobPosition'),
		},
		{
			headerName: '직위', colId: 'positionCd', field: 'positionCd', width: 110,
			headerClass: aggridHeaderClass,
			valueGetter: (params) => {
				const positionCd = params.data?.positionCd;
				const foundData = jwCdList.find((value) => value.minorCd === positionCd);
				return foundData?.codeNm;
			},
			cellStyle: (p) => getCellStyle(p, 'positionCd'),
		},
		{
			headerName: '재직', colId: 'empSts', field: 'empSts', width: 70,
			headerClass: aggridHeaderClass,
			valueGetter: (params) => {
				const empSts = params.data?.empSts || '오류';
				const foundData = empstsCdList.find((value) => value.minorCd === empSts);
				return foundData?.codeNm;
			},
			cellStyle: (p) => getCellStyle(p, 'empSts'),
		},
	], [getCellStyle, empstsCdList, jcCdList, jwCdList]);

	const defaultColumnState = useMemo(
		() => buildDefaultColumnStateFromDefs(baseColumnDefs, [])
	, [baseColumnDefs]);

	const columnDefs: ColDef<SYS3050_R01Dto>[] = useMemo(() => {
		if (columnState === null) return baseColumnDefs;
		return GridCustomColumnDefs<SYS3050_R01Dto>({
			baseColumnDefs,
			columnState,
			headerNames,
			fallbackHeaderClass: aggridHeaderClass,
		});
	}, [columnState, settingRowIndex, baseColumnDefs, headerNames]);

	const handleRowClick = useCallback((e: RowClickedEvent<SYS3050_R01Dto>) => {
		if (e.node.rowPinned === 'bottom') return;
		const rowData = e.data;
		if (!rowData) return;

		const isSelected = selectedSYS3050_R01?.empNo === rowData.empNo;
		setSelectedSYS3050_R01(isSelected ? undefined : rowData);
	}, [selectedSYS3050_R01, setSelectedSYS3050_R01]);

	const pinnedBottomRow = useMemo(() => ([{
		rowIndex: '계',
		empNo: `${SYS3050_R01List.length.toLocaleString()}건`,
		editable: false,
	}]), [SYS3050_R01List.length]);

	useEffect(() => { ensureSelectedRow(); }, [SYS3050_R01List, selectedSYS3050_R01]);

	return (
		<GridContextWrapper<SYS3050_R01Dto>
			enableCellCalculation
			gridRef={gridRef}
			paddedGridData={paddedGridData}
			initSelected={initSelectedSYS3050_R01}
			defaultRowData={defaultSYS3050_R01Row}
			baseColumnDefs={columnState ? columnDefs : baseColumnDefs}
			defaultColumnState={defaultColumnState}
			setColumnState={setColumnState}
			localStorageKey={COLUMN_STATE_KEY}
			menuItems={undefined}
		>
			<Grid_Touch<SYS3050_R01Dto>
				ref={gridRef}
				rowData={paddedGridData}
				columnDefs={columnDefs}
				defaultColDef={defaultColDef}
				getRowId={getRowId}
				rowClassRules={createRowClassRules<SYS3050_R01Dto>({
					idKey: 'empNo',
					selectedList: selectedSYS3050_R01 ? [selectedSYS3050_R01.empNo] : [],
					deletedList: [],
				})}
				onRowClicked={handleRowClick}
				onCellKeyDown={undefined}
				pinnedBottomRowData={pinnedBottomRow}
				setHoveredRowIndex={setHoveredRowIndex}
				columnStateKey={COLUMN_STATE_KEY}
				setColumnState={setColumnState}
				scrollTop={scrollTop}
				setScrollTop={setScrollTop}
			/>
		</GridContextWrapper>
	);
};
